import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { BRAND_GROUPS, ADVISED, SERVICES, POSITIONS, slug } from "../formData";
import { useLanguage } from "../i18n";
import LOGOS from "../brandLogos.json";

export const BrandPicker = ({ value, onChange }) => {
  const { t } = useLanguage();
  const b = t.brandPicker;
  const toggle = (name) => onChange(value.includes(name) ? value.filter((x) => x !== name) : [...value, name]);
  return (
    <div data-testid="brand-picker" className="space-y-4">
      {BRAND_GROUPS.map((g) => (
        <div key={g.key}>
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#C5A059] mb-2">{g.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {g.brands.map((br) => {
              const on = value.includes(br.name);
              return (
                <span key={br.name} className={`inline-flex items-center border rounded-sm h-8 text-xs transition-colors ${on ? "border-[#C5A059] bg-[#C5A059]/10 text-ink" : "border-ink/15 text-inksoft hover:border-ink/40"}`}>
                  <button type="button" data-testid={`brand-${g.key}-${slug(br.name)}`} onClick={() => toggle(br.name)} aria-pressed={on} className="pl-2.5 pr-1.5 h-full inline-flex items-center gap-1.5">{on && <Check className="w-3 h-3 text-[#C5A059]" strokeWidth={3} />}{LOGOS[slug(br.name)] && <img src={`/brands/${slug(br.name)}.png`} alt="" className="h-4 max-w-[48px] object-contain" loading="lazy" />}{br.name}</button>
                  <a href={br.url} target="_blank" rel="noopener noreferrer" aria-label={br.name} className="pr-2 text-inksoft hover:text-[#C5A059]"><ArrowUpRight className="w-3 h-3" strokeWidth={2} /></a>
                </span>
              );
            })}
          </div>
        </div>
      ))}
      <button type="button" data-testid="brand-advised" onClick={() => toggle(ADVISED)} aria-pressed={value.includes(ADVISED)}
        className={`w-full flex items-center gap-3 border rounded-sm px-4 h-11 text-sm transition-colors ${value.includes(ADVISED) ? "border-[#C5A059] bg-[#C5A059]/10 text-ink" : "border-dashed border-ink/25 text-inksoft hover:border-ink/40"}`}>
        <span className={`w-4 h-4 rounded-sm border flex items-center justify-center ${value.includes(ADVISED) ? "bg-[#C5A059] border-[#C5A059]" : "border-ink/30"}`}>{value.includes(ADVISED) && <Check className="w-3 h-3 text-ink" strokeWidth={3} />}</span>
        {b.advised}
      </button>
      <Link to="/brand-cataloghi" target="_blank" data-testid="brand-catalog-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C5A059] hover:text-ink transition-colors">{b.catalogLink}<ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} /></Link>
    </div>
  );
};

export const PointsPicker = ({ value, onChange, rooms, none = false, onNone }) => {
  const add = () => { onNone?.(false); onChange([...value, { room: rooms[0] || "", position: POSITIONS[0], qty: 1 }]); };
  const upd = (i, k, v) => onChange(value.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const sel = "h-9 rounded-sm border border-ink/15 bg-white text-sm px-2";
  return (
    <div data-testid="points-picker" className="space-y-2">
      {!none && value.map((p, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_64px_32px] gap-2 items-center">
          <select data-testid={`point-room-${i}`} value={p.room} onChange={(e) => upd(i, "room", e.target.value)} className={sel}>{rooms.map((r) => <option key={r} value={r}>{r}</option>)}</select>
          <select data-testid={`point-position-${i}`} value={p.position} onChange={(e) => upd(i, "position", e.target.value)} className={sel}>{POSITIONS.map((r) => <option key={r} value={r}>{r}</option>)}</select>
          <input data-testid={`point-qty-${i}`} type="number" min="1" value={p.qty} onChange={(e) => upd(i, "qty", e.target.value)} className={sel} />
          <button type="button" data-testid={`point-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-inksoft hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" data-testid="point-add" onClick={add} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#C5A059] hover:text-ink"><Plus className="w-3.5 h-3.5" />Aggiungi punto luce</button>
        {onNone && (
          <button type="button" data-testid="point-none" onClick={() => { onNone(!none); if (!none) onChange([]); }} aria-pressed={none}
            className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] border rounded-sm px-3 h-8 transition-colors ${none ? "border-[#C5A059] bg-[#C5A059]/10 text-ink" : "border-ink/15 text-inksoft hover:border-ink/40"}`}>
            <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center ${none ? "bg-[#C5A059] border-[#C5A059]" : "border-ink/30"}`}>{none && <Check className="w-2.5 h-2.5 text-ink" strokeWidth={3} />}</span>Nessuno
          </button>
        )}
      </div>
      {none && <p data-testid="point-none-note" className="text-xs text-inksoft">Nessun punto luce esistente o previsto, proporremo noi la disposizione da zero.</p>}
    </div>
  );
};

export const InfoModal = ({ open, onClose, title, text, cta, testid = "info-modal" }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid={testid} className="bg-[#F7F5F0] text-ink border-[#C5A059]/50 rounded-sm max-w-lg p-8">
      <DialogHeader><DialogTitle className="font-display text-2xl font-medium tracking-tight text-ink">{title}</DialogTitle></DialogHeader>
      <p className="text-sm text-inksoft leading-relaxed">{text}</p>
      <button type="button" data-testid={`${testid}-ok`} onClick={onClose} className="w-full mt-2 bg-ink text-cream hover:bg-[#C5A059] hover:text-ink transition-colors rounded-sm h-11 text-xs font-semibold uppercase tracking-[0.15em]">{cta}</button>
    </DialogContent>
  </Dialog>
);

export const UploadGuide = ({ open, onClose, onProceed }) => {
  const { t } = useLanguage();
  const g = t.uploadGuide;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="upload-guide" className="bg-[#F7F5F0] text-ink border-[#C5A059]/50 rounded-sm max-w-4xl p-8 max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C5A059]">{g.overline}</p>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight text-ink">{g.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-inksoft leading-relaxed">{g.intro}</p>
        <div className="grid md:grid-cols-[1.1fr_1fr] gap-6 items-start">
        <figure data-testid="upload-guide-example" className="bg-white border border-[#C5A059]/40 rounded-sm p-2">
          <img src="/plan-example.jpg" alt="Esempio di pianta arredata con le stanze nominate" className="w-full h-auto rounded-sm" />
          <figcaption className="mt-2 text-[11px] text-inksoft text-center">Esempio: pianta arredata con studio, living, cucina, zona pranzo, ripostiglio, camera 2, bagno 1, bagno 2, camera padronale</figcaption>
        </figure>
        <ol className="space-y-4 mt-2">
          {g.steps.map((s, i) => (
            <li key={i} className="flex gap-4">
              <span className="font-display text-2xl text-[#C5A059] leading-none">{i + 1}</span>
              <div><p className="text-sm font-semibold text-ink">{s.title}</p><p className="text-sm text-inksoft leading-relaxed">{s.desc}</p></div>
            </li>
          ))}
        </ol>
        </div>
        <button type="button" data-testid="upload-guide-proceed" onClick={onProceed} className="w-full mt-4 bg-ink text-cream hover:bg-[#C5A059] hover:text-ink transition-colors rounded-sm h-12 text-xs font-semibold uppercase tracking-[0.15em]">{g.cta}</button>
      </DialogContent>
    </Dialog>
  );
};

const SUGGESTED = {
  residenziale: ["Flos", "Artemide", "Foscarini", "Davide Groppi"], retail: ["iGuzzini", "Viabizzuno", "Flos Architectural", "Artemide"], locali: ["Vibia", "Flos", "Davide Groppi", "Viabizzuno"],
  ospitalita: ["Vibia", "Flos", "Davide Groppi", "Viabizzuno"], uffici: ["iGuzzini", "Trilux", "Zumtobel"], esterni: ["iGuzzini", "Flos", "Viabizzuno", "Vibia"],
};

export const BrandLogo = ({ name, className = "" }) => {
  const key = slug(name);
  const kind = LOGOS[key];
  if (kind === "logo") return <img src={`/brands/${key}.png`} alt={name} data-testid={`brand-logo-${key}`} className={`block w-full max-w-[160px] h-10 object-contain ${className}`} loading="lazy" />;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} data-testid={`brand-logo-${key}`}>
      {kind === "icon" && <img src={`/brands/${key}.png`} alt="" className="w-8 h-8 object-contain rounded-sm" loading="lazy" />}
      <span className="font-display text-sm tracking-wide uppercase text-ink">{name}</span>
    </span>
  );
};

export const SuggestedBrands = ({ spaceType, value, onChange, free, onFree, includeOutdoor = false }) => {
  const { t } = useLanguage();
  const b = t.brandPicker;
  const list = [...new Set([...(SUGGESTED[spaceType] || SUGGESTED.residenziale), ...(includeOutdoor && spaceType !== "esterni" ? SUGGESTED.esterni : [])])];
  const toggle = (name) => onChange(value.includes(name) ? value.filter((x) => x !== name) : [...value, name]);
  const addFree = () => {
    const name = free.trim().replace(/\s+/g, " ");
    if (!name) return;
    if (!value.some((v) => v.toLowerCase() === name.toLowerCase())) onChange([...value, name]);
    onFree("");
  };
  return (
    <div data-testid="suggested-brands" className="space-y-3">
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#C5A059]">{b.suggested}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {list.map((name) => {
          const on = value.includes(name);
          return (
            <button key={name} type="button" title={name} data-testid={`brand-suggested-${slug(name)}`} onClick={() => toggle(name)} aria-pressed={on}
              className={`group relative h-20 border rounded-sm flex items-center justify-center px-3 transition-colors ${on ? "border-[#C5A059] bg-[#C5A059]/10" : "border-ink/15 hover:border-ink/40 bg-white"}`}>
              <BrandLogo name={name} />
              <span className="pointer-events-none absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity text-[10px] bg-ink text-cream px-2 py-0.5 rounded-sm whitespace-nowrap">{name}</span>
              {on && <Check className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-[#C5A059]" strokeWidth={2.5} />}
            </button>
          );
        })}
      </div>
      <div className="space-y-1 pt-2">
        <p className="text-xs font-semibold text-ink">{b.free}</p>
        <div className="flex gap-2">
          <input data-testid="brand-free-input" value={free} onChange={(e) => onFree(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFree(); } }} placeholder={b.freePlaceholder} className="flex-1 h-11 rounded-sm border border-ink/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40" />
          <button type="button" data-testid="brand-free-add" onClick={addFree} disabled={!free.trim()} className="h-11 px-4 rounded-sm bg-ink text-cream hover:bg-[#C5A059] hover:text-ink disabled:opacity-40 text-[10px] font-semibold uppercase tracking-[0.15em]">{b.addFree}</button>
        </div>
      </div>
    </div>
  );
};

export const BrandsByArea = ({ areas, spaceType, value, onChange, globalBrands, includeOutdoor = false }) => {
  const { t } = useLanguage();
  const b = t.brandPicker;
  const list = [...new Set([...(SUGGESTED[spaceType] || SUGGESTED.residenziale), ...(includeOutdoor && spaceType !== "esterni" ? SUGGESTED.esterni : []), ...globalBrands])];
  const toggle = (area, name) => {
    const cur = value[area] || [];
    onChange({ ...value, [area]: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] });
  };
  if (!areas.length) return null;
  return (
    <div data-testid="brands-by-area" className="space-y-2 pt-2">
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#C5A059]">{b.byArea}</p>
      <p className="text-xs text-inksoft">{b.byAreaHint}</p>
      <div className="space-y-1.5">
        {areas.map((area) => (
          <div key={area} data-testid={`brands-area-${slug(area)}`} className="flex flex-wrap items-center gap-1.5 bg-white border border-ink/10 rounded-sm px-3 py-2">
            <span className="text-xs font-semibold text-ink mr-2 min-w-[110px]">{area}</span>
            {list.map((name) => {
              const on = (value[area] || []).includes(name);
              return <button key={name} type="button" data-testid={`brand-area-${slug(area)}-${slug(name)}`} onClick={() => toggle(area, name)} aria-pressed={on} className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${on ? "border-[#C5A059] bg-[#C5A059]/10 text-ink" : "border-ink/15 text-inksoft hover:border-ink/40"}`}>{name}</button>;
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export const RoomsList = ({ value, onChange, options = [] }) => {
  const { t } = useLanguage();
  const w = t.twin;
  const update = (i, v) => onChange(value.map((r, j) => (j === i ? v : r)));
  return (
    <div data-testid="rooms-list" className="space-y-2">
      <p className="text-xs text-inksoft">{w.roomsHint}</p>
      {value.map((r, i) => (
        <div key={i} className="flex gap-2">
          {options.length > 0 && (
            <select data-testid={`concept-room-select-${i}`} value="" onChange={(e) => { if (e.target.value) update(i, e.target.value); }} aria-label={w.roomPick} className="h-11 w-44 sm:w-56 rounded-sm border border-ink/15 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40">
              <option value="">{w.roomPick}</option>
              {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          <input data-testid={`room-input-${i}`} value={r} onChange={(e) => update(i, e.target.value)} placeholder={w.roomPlaceholder} className="flex-1 h-11 rounded-sm border border-ink/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40" />
          {value.length > 1 && <button type="button" data-testid={`room-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="px-3 text-inksoft hover:text-ink"><X className="w-4 h-4" /></button>}
        </div>
      ))}
      <button type="button" data-testid="room-add" onClick={() => onChange([...value, ""])} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C5A059] hover:text-ink"><Plus className="w-3.5 h-3.5" strokeWidth={2.5} />{w.roomAdd}</button>
    </div>
  );
};

export const ServiceChoice = ({ plan, onPlan, addStudio, onAddStudio }) => {
  const { t } = useLanguage();
  const w = t.twin;
  const [open, setOpen] = React.useState(null);
  return (
    <div data-testid="service-choice" className="grid sm:grid-cols-2 gap-3">
      {[["base", w.base, w.baseDesc], ["advanced", w.advanced, w.advancedDesc]].map(([k, name, desc]) => {
        const on = plan === k;
        return (
          <div key={k} data-testid={`service-choice-${k}`} className={`border rounded-sm p-4 transition-colors ${on ? "border-[#C5A059] bg-[#C5A059]/10" : "border-ink/15 bg-white"}`}>
            <button type="button" data-testid={`service-choice-${k}-select`} onClick={() => onPlan(k)} aria-pressed={on} className="w-full text-left flex items-center gap-3">
              <span className={`w-4 h-4 shrink-0 rounded-full border flex items-center justify-center ${on ? "bg-[#C5A059] border-[#C5A059]" : "border-ink/30"}`}>{on && <Check className="w-3 h-3 text-ink" strokeWidth={3} />}</span>
              <span className="text-sm font-semibold text-ink">{name}</span>
            </button>
            <button type="button" data-testid={`service-choice-${k}-toggle`} onClick={() => setOpen(open === k ? null : k)} className="mt-2 text-xs font-semibold text-[#C5A059] hover:text-ink">{open === k ? w.less : w.more}</button>
            {open === k && <p data-testid={`service-choice-${k}-desc`} className="mt-2 text-xs text-inksoft leading-relaxed">{desc}</p>}
            {k === "advanced" && on && (
              <label className="mt-3 flex items-start gap-2 text-xs text-ink cursor-pointer">
                <input type="checkbox" data-testid="service-add-studio" checked={addStudio} onChange={(e) => onAddStudio(e.target.checked)} className="mt-0.5 w-3.5 h-3.5 accent-[#C5A059]" />
                <span>{w.addStudio}<br /><span className="text-inksoft">{w.onlyAdvanced}</span></span>
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
};
